
import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { getBlogPostBySlug } from "@/data/blog-posts";
import { Calendar, Tag, ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SEOHead } from "@/components/seo/SEOHead";
import { generateEnhancedArticleSchema, generateBreadcrumbSchema } from "@/lib/schema";
import { BreadcrumbNavigation } from "@/components/ui/breadcrumb-list";

export default function BlogPostPage() {
    const { slug } = useParams();
    const post = slug ? getBlogPostBySlug(slug) : null;
    
    if (!post) {
        return <Navigate to="/blog" replace />;
    }

    const breadcrumbs = [
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
        { name: post.title, url: `/blog/${slug}` }
    ];
    
    const articleSchema = generateEnhancedArticleSchema({
        title: post.title,
        description: post.excerpt,
        imageUrl: post.imageUrl || "https://www.grandpal.ie/lovable-uploads/b67e8743-f24e-45cd-ba77-2a1ae609411c.png",
        datePublished: post.date,
        author: {
            name: "Grandpal Team",
            url: "https://www.grandpal.ie/about"
        },
        url: `/blog/${slug}`
    });
    
    const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
    
    const combinedSchema = [articleSchema, breadcrumbSchema];
    
    return (
        <Layout>
            <SEOHead 
                title={`${post.title} | Grandpal Blog`}
                description={post.excerpt}
                canonicalUrl={`/blog/${slug}`}
                ogImage={post.imageUrl || "/lovable-uploads/b67e8743-f24e-45cd-ba77-2a1ae609411c.png"}
                ogType="article"
                schema={combinedSchema}
            />
            
            <div className="bg-grandpal-beige/30 py-16 md:py-24">
                <div className="container max-w-4xl">
                    <Link to="/blog" className="inline-flex items-center gap-2 text-grandpal-gray hover:text-grandpal-green transition-colors mb-4">
                        <ArrowLeft className="w-4 h-4" />
                        Back to articles
                    </Link>
                    
                    <BreadcrumbNavigation items={breadcrumbs.map(b => ({ label: b.name, href: b.url }))} />

                    <article className="bg-white rounded-2xl shadow-sm border border-grandpal-beige p-6 md:p-10 mt-4">
                        {post.imageUrl && (
                            <div className="relative h-[350px] md:h-[410px] -mx-6 md:-mx-10 -mt-6 md:-mt-10 mb-8 rounded-t-2xl overflow-hidden">
                                <img src={post.imageUrl} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
                            </div>
                        )}
                        
                        <div className="flex flex-wrap items-center gap-6 text-grandpal-gray mb-6">
                            <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span>{post.date}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Tag className="h-4 w-4" />
                                <div className="flex flex-wrap gap-2">
                                    {post.categories.map(category => (
                                        <span key={category} className="text-sm px-3 py-1 bg-grandpal-beige text-grandpal-green-dark rounded-full">
                                            {category}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <h1 className="text-4xl md:text-5xl font-semibold mb-6 text-grandpal-green-dark">
                            {post.title}
                        </h1>
                        
                        <div className="prose prose-lg max-w-none prose-headings:text-grandpal-green prose-a:text-grandpal-green prose-strong:text-grandpal-green prose-p:mt-2 prose-p:mb-4">
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    h2: ({node, ...props}) => <h2 className="mt-10 mb-2 text-2xl font-semibold text-grandpal-green-dark" {...props} />,
                                    h3: ({node, ...props}) => <h3 className="mt-8 mb-2 text-xl font-semibold text-grandpal-green-dark" {...props} />,
                                    ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4" {...props} />,
                                    ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4" {...props} />,
                                    li: ({node, ...props}) => <li className="mb-1" {...props} />,
                                    blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-grandpal-green bg-grandpal-green-lighter px-4 py-2 my-4 italic text-grandpal-green-dark" {...props} />,
                                }}
                            >
                                {post.content}
                            </ReactMarkdown>
                        </div>
                    </article>
                    
                    <div className="mt-16 text-center">
                        <Link to="/blog" className="inline-flex items-center gap-2 text-grandpal-green hover:text-grandpal-green-dark transition-colors">
                            <ArrowLeft className="w-4 h-4" />
                            Back to all articles
                        </Link>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
